import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';

import { Controller, DeepMap, FieldError } from 'react-hook-form';
import { FormGroup, Input } from 'components/chroma';
import config, { FeatureToggleState } from 'shared/config';
import { QuestionProps } from 'scenes/common/components/Questions';

import {
  setQuoteIdPattern,
  allowablePrefix,
  regexPrefixMatch,
} from './helper/quotePrefix';

const MAX_RETRIEVE_QUOTE_DISPLAY_LENGTH = 18;
const MAX_RETRIEVE_QUOTE_ID_LENGTH = 12;
const PREFIX_LENGTH = 4;

const QUOTE_ID_PATTERN = setQuoteIdPattern(
  config.featureToggles.homeRetrieve === FeatureToggleState.ON,
  config.featureToggles.landlordRetrieve === FeatureToggleState.ON
);

const ALLOWABLE_PREFIXES = allowablePrefix(
  config.featureToggles.homeRetrieve === FeatureToggleState.ON,
  config.featureToggles.landlordRetrieve === FeatureToggleState.ON
);
interface RetrieveQuoteQuestionProps extends QuestionProps<string> {
  stateKey: string;
  ariaLabel?: string;
  placeholder: string;
  question: string;
  errors: DeepMap<Record<string, unknown>, FieldError>;
}

const RetrieveQuoteQuestion: FunctionComponent<RetrieveQuoteQuestionProps> = ({
  stateKey,
  question,
  ariaLabel,
  placeholder,
  errors,
  control,
  defaultValue,
  changeHandler,
}) => {
  const addHyphensToQuoteId = (quoteId: string): string =>
    quoteId
      .replace(/(.{4})/g, '$1 - ')
      .slice(0, MAX_RETRIEVE_QUOTE_DISPLAY_LENGTH);

  const [quoteNumberDisplay, setQuoteNumberDisplay] = useState(
    defaultValue ? addHyphensToQuoteId(defaultValue) : ''
  );

  useEffect(() => {
    setQuoteNumberDisplay(addHyphensToQuoteId(defaultValue));
  }, [defaultValue]);

  const formatQuoteNumber = (
    quoteNumber: string,
    quoteNoDisplay: string
  ): string => {
    let formattedQuoteNumber = quoteNumber.trim();
    // handle backspace
    if (
      formattedQuoteNumber[formattedQuoteNumber.length - 1] === '-' &&
      formattedQuoteNumber.length - quoteNoDisplay.length < 0
    ) {
      formattedQuoteNumber = quoteNumber.slice(0, -3);
    }

    // remove hyphens
    const withoutHyphen = formattedQuoteNumber.replace(/ - /g, '');

    // value is the best substring prefix match between allowable prefixes
    // and value input.

    const prefix = withoutHyphen
      .substr(0, PREFIX_LENGTH)
      .toUpperCase()
      .split('')
      .map((char, index) =>
        regexPrefixMatch(
          ALLOWABLE_PREFIXES,
          withoutHyphen.toUpperCase().substr(0, index + 1)
        )
          ? char
          : ''
      )
      .join('');
    // compute numeric portion of the quote id
    const numericString = withoutHyphen
      .substr(PREFIX_LENGTH)
      .replace(/\D/g, '');

    formattedQuoteNumber = `${prefix}${numericString}`;
    setQuoteNumberDisplay(addHyphensToQuoteId(formattedQuoteNumber));
    return formattedQuoteNumber;
  };

  const handleQuoteNumber = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: () => void
  ) => {
    const {
      target: { value },
    } = event;
    // @ts-ignore
    changeHandler(formatQuoteNumber(value, quoteNumberDisplay), onChange);
  };

  return (
    <FormGroup id={stateKey} label={question}>
      <Controller
        control={control}
        name={stateKey}
        defaultValue={defaultValue}
        rules={{
          required: true,
          validate: {
            length: (value) => value.length === MAX_RETRIEVE_QUOTE_ID_LENGTH,
          },
          pattern: QUOTE_ID_PATTERN,
        }}
        render={({ onChange, value }) => (
          <Input
            id={stateKey}
            value={quoteNumberDisplay}
            name={stateKey}
            ariaLabel={ariaLabel}
            maxLength={MAX_RETRIEVE_QUOTE_DISPLAY_LENGTH}
            placeholder={placeholder}
            error={Boolean(value) && Boolean(errors[stateKey])}
            valid={
              quoteNumberDisplay.length === MAX_RETRIEVE_QUOTE_DISPLAY_LENGTH
            }
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleQuoteNumber(event, onChange)
            }
          />
        )}
      />
    </FormGroup>
  );
};

export default RetrieveQuoteQuestion;
